"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { DEFAULT_IMAGE, IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

import { contactFormChildRequirements } from "../ContactForm/ContactForm.builder";

export const loyaltyContactFormInputs = [
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "How can we help you?",
  },
  {
    name: "description",
    type: "string",
    required: true,
    defaultValue: "The Ploom Care Team is here to help. Give us a call or use the below contact form.",
  },
  {
    name: "disclaimer",
    type: "string",
    defaultValue: "* Fields required",
  },
  {
    name: "contestId",
    type: "string",
    required: true,
  },
  {
    name: "errorMessage",
    type: "string",
    defaultValue: "Error occured, please try again later",
  },
  {
    name: "hiddenFields",
    type: "list",
    subFields: [
      {
        name: "key",
        type: "string",
        required: true,
      },
      {
        name: "value",
        type: "string",
        required: true,
      },
    ],
  },
  {
    name: "successScreenTitle",
    type: "string",
    defaultValue: "Success screen title",
  },
  {
    name: "successScreenButtonLabel",
    type: "string",
    defaultValue: "Send another message",
  },
  {
    name: "successScreenHref",
    type: "string",
    defaultValue: "/",
  },
  {
    name: "successFormHeading",
    type: "string",
    friendlyName: "Form heading on success state",
    required: true,
    defaultValue: "Thank you for joining us on this exciting journey",
  },
  {
    name: "successFormSubHeading",
    type: "string",
    friendlyName: "Form subheading on success state",
    required: true,
    defaultValue:
      "You're now one step closer to winning [REWARDS]. Keep an eye out for updates, announcements, and more",
  },
  {
    name: "successHeading",
    type: "string",
    friendlyName: "Success Heading, should contain {#Name#} to display user name",
    required: true,
    defaultValue: "{#Name#}, you're now in with a chance of winning [REWARDS].",
  },
  {
    name: "successScreenDescription",
    friendlyName: "Success Screen Description",
    type: "string",
    required: true,
    defaultValue: "Simply click 'Participate' to enter",
  },
  {
    name: "successSubHeading",
    friendlyName: "Success Sub Heading",
    type: "string",
    required: true,
    defaultValue: "Your entry is officially recorded.",
  },
  {
    name: "sendButtonLabel",
    type: "string",
    required: true,
    defaultValue: "Participate",
  },
  {
    name: "oneTrust",
    type: "object",
    required: true,
    subFields: [
      {
        name: "consent",
        type: "string",
        required: true,
        enum: ["registration", "dashboard", "subscription", "contact_form", "set_password", "all", "quiz", "sob"],
      },
    ],
    defaultValue: {
      consent: "contact_form",
    },
  },
  {
    name: "content",
    type: "object",
    required: true,
    subFields: [
      {
        name: "heading",
        type: "string",
        required: true,
      },
      {
        name: "subHeading",
        type: "string",
        required: true,
      },
    ],
    defaultValue: {
      heading: "Ready to participate and win [Reward]",
      subHeading: "Sign up now to start earning rewards",
    },
  },
  {
    name: "image",
    type: "object",
    required: true,
    subFields: [
      {
        name: "rightImage",
        type: "file",
        allowedFileTypes: IMAGE_FILE_TYPES,
        required: true,
      },
    ],
    defaultValue: {
      rightImage: DEFAULT_IMAGE,
    },
  },
  {
    name: "theme",
    type: "string",
    enum: ["light", "dark"],
  },
] as const satisfies Inputs;

export const LoyaltyContactFormComponent: RegisteredComponent = {
  name: "LoyaltyContactForm",
  component: dynamic(async () =>
    withValidation(
      loyaltyContactFormInputs,
      (await import("./LoyaltyContactForm")).LoyaltyContactForm,
      "LoyaltyContactForm"
    )
  ),
  canHaveChildren: true,
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderContext: true,
  },
  inputs: loyaltyContactFormInputs,
  childRequirements: {
    message: "You can only put Form components inside this component",
    query: {
      "component.name": {
        $in: [
          ...contactFormChildRequirements.query["component.name"].$in,
          "LoyaltyContactFormCheckboxSection",
          "LoyaltyContactFormTextArea",
        ],
      },
    },
  },
};
